import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import AIAgentLauncher from './AIAgentLauncher';
import AIAgentDrawer from './AIAgentDrawer';
import {
  aiAgentReducer,
  canSendMessage,
  createInitialConversation,
  createQuestionId,
  createRequestId,
  getOriginalQuestionContent,
} from './AIAgentReducer';
import { useAIAgentScreenContext } from './hooks/useAIAgentScreenContext';
import {
  clearStoredConversationId,
  ensureSession,
  getStoredConversationId,
  replaceSession,
  sendMessage,
  AIAgentConfigurationError,
  AIAgentMalformedResponseError,
  AIAgentUnauthorizedError,
  AIAgentValidationError,
} from './services/AIAgentService';
import {
  persistConversationState,
  restoreConversationState,
} from './services/AIAgentConversationStorage';
import {
  mapAIAgentError,
  mapConfigurationError,
  mapInvalidIdentityError,
  mapMalformedSuccessResponse,
  mapResetFailedError,
} from './AIAgentErrorMapper';
import { AIAgentIdentityStatus } from './AIAgentTypes';
import { AIAgentScreenSelections } from './hooks/useAIAgentScreenContext';
import { AUTHORITIES } from '../../Config/Constants';
import { hasPermission } from '../../Shared/Auth/AuthenticationUtil';

interface Props {
  accountResolved: boolean;
  authoritySet?: { name: string }[];
  screenSelections?: AIAgentScreenSelections;
}

function resolveIdentityStatus(accountResolved: boolean): AIAgentIdentityStatus {
  if (!accountResolved) {
    return 'resolving';
  }
  return 'valid';
}

export default function AIAgentHost({
  accountResolved,
  authoritySet,
  screenSelections,
}: Props) {
  const effectiveAuthoritySet = Array.isArray(authoritySet) ? authoritySet : [];
  const canUseAiChat = hasPermission(effectiveAuthoritySet, [
    AUTHORITIES.VIEW_AI_CHAT,
  ]);
  if (!canUseAiChat) {
    return null;
  }
  return (
    <AIAgentHostConversation
      accountResolved={accountResolved}
      screenSelections={screenSelections}
    />
  );
}

function AIAgentHostConversation({
  accountResolved,
  screenSelections,
}: Props) {
  const screenContext = useAIAgentScreenContext(screenSelections);
  const identityStatus = useMemo(
    () => resolveIdentityStatus(accountResolved),
    [accountResolved]
  );

  const [state, dispatch] = useReducer(
    aiAgentReducer,
    { screenContext, identityStatus },
    ({ screenContext: initialScreenContext, identityStatus: initialIdentityStatus }) =>
      restoreConversationState(
        getStoredConversationId(),
        initialScreenContext,
        initialIdentityStatus
      ) ?? createInitialConversation(initialScreenContext, initialIdentityStatus)
  );

  const launcherRef = useRef<HTMLButtonElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const chatInFlightRef = useRef(false);
  const resetInFlightRef = useRef(false);
  const sessionReadyRef = useRef(false);
  const sessionInitInFlightRef = useRef(false);
  const openEpochRef = useRef(0);

  useEffect(() => {
    dispatch({ type: 'SET_IDENTITY_STATUS', identityStatus });
  }, [identityStatus]);

  useEffect(() => {
    dispatch({ type: 'SET_SCREEN_CONTEXT', screenContext });
  }, [screenContext]);

  useEffect(() => {
    if (identityStatus === 'valid') {
      persistConversationState(state);
    }
  }, [identityStatus, state]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      dispatch({ type: 'DISCARD' });
    };
  }, []);

  const abortActiveRequest = useCallback((clearChatInFlight = true) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    if (clearChatInFlight) {
      chatInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (identityStatus === 'valid') {
      return;
    }
    openEpochRef.current += 1;
    abortActiveRequest();
    sessionInitInFlightRef.current = false;
    sessionReadyRef.current = false;
    clearStoredConversationId();
  }, [abortActiveRequest, identityStatus]);

  const initializeSession = useCallback(async () => {
    if (identityStatus !== 'valid' || sessionInitInFlightRef.current) {
      return;
    }

    const openEpoch = openEpochRef.current;
    sessionInitInFlightRef.current = true;
    dispatch({ type: 'SESSION_INITIALIZING' });
    sessionReadyRef.current = false;

    try {
      const conversationId = await ensureSession();
      if (openEpoch !== openEpochRef.current) {
        return;
      }
      sessionReadyRef.current = true;
      dispatch({ type: 'SESSION_READY', conversationId });
    } catch (error) {
      if (openEpoch !== openEpochRef.current) {
        return;
      }
      sessionReadyRef.current = false;
      clearStoredConversationId();
      dispatch({ type: 'SESSION_INVALID' });

      if (error instanceof AIAgentConfigurationError) {
        dispatch({
          type: 'RESET_FAILURE',
          message: mapConfigurationError().message,
        });
        return;
      }

      if (error instanceof AIAgentUnauthorizedError) {
        dispatch({
          type: 'SET_IDENTITY_STATUS',
          identityStatus: 'invalid',
        });
        return;
      }

      const mapped = mapAIAgentError(error);
      dispatch({
        type: 'RESET_FAILURE',
        message: mapped?.message ?? mapResetFailedError().message,
      });
    } finally {
      sessionInitInFlightRef.current = false;
    }
  }, [identityStatus]);

  useEffect(() => {
    if (state.mode === 'closed' || identityStatus !== 'valid') {
      return;
    }

    if (state.phase === 'resetting') {
      return;
    }

    if (state.session.status === 'invalid') {
      return;
    }

    if (state.session.conversationId) {
      sessionReadyRef.current = true;
      return;
    }

    void initializeSession();
  }, [
    identityStatus,
    initializeSession,
    state.mode,
    state.phase,
    state.session.conversationId,
    state.session.status,
  ]);

  const waitForSessionId = useCallback(async (): Promise<string | null> => {
    if (state.session.conversationId && state.session.status === 'active') {
      return state.session.conversationId;
    }

    try {
      const conversationId = await ensureSession();
      sessionReadyRef.current = true;
      dispatch({ type: 'SESSION_READY', conversationId });
      return conversationId;
    } catch {
      clearStoredConversationId();
      sessionReadyRef.current = false;
      dispatch({ type: 'SESSION_INVALID' });
      return null;
    }
  }, [state.session.conversationId, state.session.status]);

  const callChatApi = useCallback(
    async (
      message: string,
      questionId: string,
      requestId: string,
      conversationEpoch: number
    ) => {
      if (identityStatus !== 'valid') {
        return;
      }

      abortActiveRequest(false);
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const conversationId = await waitForSessionId();
        if (!conversationId) {
          dispatch({
            type: 'SEND_ERROR',
            requestId,
            questionId,
            conversationEpoch,
            error: {
              ...mapInvalidIdentityError(),
              kind: 'session-invalid',
              category: 'SESSION_INVALID',
              message:
                'A sessão desta conversa não é mais válida. Inicie uma nova conversa para continuar.',
              action: 'start-new-conversation',
              isSessionInvalid: true,
            },
          });
          return;
        }

        if (controller.signal.aborted) {
          dispatch({ type: 'STALE_COMPLETION' });
          return;
        }

        const result = await sendMessage(
          message,
          conversationId,
          controller.signal
        );

        dispatch({
          type: 'SEND_SUCCESS',
          requestId,
          questionId,
          conversationEpoch,
          reply: result.reply,
          diagnostics: result.diagnostics,
        });
      } catch (error) {
        if (error instanceof AIAgentValidationError) {
          dispatch({ type: 'STALE_COMPLETION' });
          return;
        }

        if (error instanceof AIAgentMalformedResponseError) {
          dispatch({
            type: 'SEND_ERROR',
            requestId,
            questionId,
            conversationEpoch,
            error: mapMalformedSuccessResponse(),
          });
          return;
        }

        if (error instanceof AIAgentConfigurationError) {
          dispatch({
            type: 'SEND_ERROR',
            requestId,
            questionId,
            conversationEpoch,
            error: mapConfigurationError(),
          });
          return;
        }

        if (error instanceof AIAgentUnauthorizedError) {
          dispatch({
            type: 'SEND_ERROR',
            requestId,
            questionId,
            conversationEpoch,
            error: mapInvalidIdentityError(),
          });
          return;
        }

        const mapped = mapAIAgentError(error);
        if (!mapped) {
          dispatch({ type: 'STALE_COMPLETION' });
          return;
        }

        if (mapped.isSessionInvalid) {
          clearStoredConversationId();
          sessionReadyRef.current = false;
        }

        dispatch({
          type: 'SEND_ERROR',
          requestId,
          questionId,
          conversationEpoch,
          error: mapped,
        });
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
        chatInFlightRef.current = false;
      }
    },
    [abortActiveRequest, identityStatus, waitForSessionId]
  );

  const executeSend = useCallback(
    async (
      message: string,
      questionId: string,
      requestId: string,
      conversationEpoch: number
    ) => {
      if (identityStatus !== 'valid' || chatInFlightRef.current) {
        return;
      }

      chatInFlightRef.current = true;
      dispatch({
        type: 'SEND_START',
        questionId,
        requestId,
        message,
        conversationEpoch,
      });

      await callChatApi(message, questionId, requestId, conversationEpoch);
    },
    [callChatApi, identityStatus]
  );

  const handleSend = useCallback(
    (messageOverride?: string) => {
      if (!canSendMessage(state) || identityStatus !== 'valid') {
        return;
      }

      if (state.session.status === 'invalid') {
        return;
      }

      const message = (messageOverride ?? state.draft).trim();
      if (!message) {
        return;
      }

      const questionId = createQuestionId();
      const requestId = createRequestId();
      void executeSend(message, questionId, requestId, state.conversationEpoch);
    },
    [executeSend, identityStatus, state]
  );

  const handleRetry = useCallback(
    (questionId: string) => {
      if (
        identityStatus !== 'valid' ||
        state.phase === 'sending' ||
        chatInFlightRef.current
      ) {
        return;
      }

      const original = getOriginalQuestionContent(state, questionId);
      if (!original) {
        return;
      }

      const requestId = createRequestId();
      chatInFlightRef.current = true;
      dispatch({ type: 'RETRY', requestId, questionId });
      void callChatApi(
        original,
        questionId,
        requestId,
        state.conversationEpoch
      );
    },
    [callChatApi, identityStatus, state]
  );

  const handleStartNewConversation = useCallback(async () => {
    if (
      identityStatus !== 'valid' ||
      state.phase === 'resetting' ||
      resetInFlightRef.current
    ) {
      return;
    }

    resetInFlightRef.current = true;
    openEpochRef.current += 1;
    abortActiveRequest();
    dispatch({ type: 'RESET_START' });
    sessionReadyRef.current = false;

    try {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const conversationId = await replaceSession(controller.signal);
      dispatch({ type: 'RESET_SUCCESS', conversationId });
      sessionReadyRef.current = true;
    } catch {
      clearStoredConversationId();
      dispatch({
        type: 'RESET_FAILURE',
        message: mapResetFailedError().message,
      });
    } finally {
      abortControllerRef.current = null;
      resetInFlightRef.current = false;
    }
  }, [abortActiveRequest, identityStatus, state.phase]);

  const handleOpen = useCallback(() => {
    dispatch({ type: 'OPEN' });
  }, []);

  const isDrawerOpen = state.mode !== 'closed';

  return (
    <>
      {!isDrawerOpen && (
        <AIAgentLauncher
          launcherRef={launcherRef}
          onOpen={handleOpen}
        />
      )}
      <AIAgentDrawer
        state={state}
        restoreFocusRef={launcherRef}
        onClose={() => dispatch({ type: 'CLOSE' })}
        onExpand={() => dispatch({ type: 'EXPAND' })}
        onReturnNormal={() => dispatch({ type: 'RETURN_NORMAL' })}
        onStartNewConversation={() => void handleStartNewConversation()}
        onDraftChange={(draft) => dispatch({ type: 'SET_DRAFT', draft })}
        onSend={() => handleSend()}
        onSuggestionSelect={(text) => handleSend(text)}
        onRetry={handleRetry}
      />
    </>
  );
}
